import { action, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { isNumber } from 'lodash';
import * as React from 'react';

import ActionableDivider from '../actionable-divider';
import EdittableCard from './EdittableCard';
import InputField from '../forms/input-field';
import Modal from '../modal';
import request from '../utils/Request';
import Select from '../forms/select';

import { Education, EducationEditorProps} from '../../types';
import EducationEditorStore from '../../stores/EducationEditorStore';

@inject('educationEditorStore')
@observer
class EducationEditor extends React.Component<EducationEditorProps> {
  private store: EducationEditorStore;

  public constructor (props: EducationEditorProps) {
    super(props);
    this.store = props.educationEditorStore!;
  }

  public render() {
    return (
      <>
        {/* Get the add new block modal on the page */}
        {this.createEducationModal()}

        {/* render the blocks first */}
        <div className="columns is-multiline edittable-card-container is-marginless">
          {this.renderEducations()}
        </div>

        {/* allow adding new blocks for the form */}
        <ActionableDivider
            action={this.showModal}
            actionBoxClassName="has-background-primary"
            className="column is-12"
        >
            <span className="icon has-text-white"><i className="material-icons is-size-4">add</i></span>
        </ActionableDivider>

      </>
    );
  }

  private renderEducations = () => {
    const blocks = this.store.educations.map((education, blockId) => {
      return (
        <EdittableCard
            blockId={blockId}
            key={`education-${blockId}`}
            onDeleteClick={this.handleDeleteClick}
            onEditClick={this.handleEditClick}
        >
          <p className="title is-size-6">{education.school}</p>
          <p className="subtitle is-size-6">{education.major}</p>
          <p className="is-size-6">{`${education.degree}, ${education.gradYear}`}</p>
        </EdittableCard>
      );
    });

    // Indicates to user that the list is empty
    const emptyPrompt =
        <div className="column is-12 has-text-centered has-text-grey">
          <div className="empty-block-prompt-icon"><i className="fas fa-graduation-cap" /></div>
          <p className="is-size-5 has-text-weight-bold">Add your education to the list</p>
          <p className="is-size-6">Your education history help us improve your matches ;)</p>
        </div>;

    return blocks.length > 0 ? blocks : emptyPrompt;
  }

  private createEducationModal = () => {
    const gradYearString = !this.store.gradYear ?  '' : `${this.store.gradYear}`;
    
    return (
      <Modal isActive={this.store.isEditModalActive}>
        <h3 className="title is-size-5">Add education</h3>
        <div className="columns">
          <InputField
              label="School Name"
              layoutClassName="column is-12"
              helpMessage={this.store.schoolHelpMessage}
              highlight={this.store.schoolHighlight}
              name="school"
              onChange={this.handleSchoolChange}
              placeholder="University of Yolo"
              type="text"
              value={this.store.school}
          />
        </div>

        <div className="columns">
          <Select
              label="Degree"
              layoutClassName="column is-6"
              helpMessage={this.store.degreeHelpMessage}
              highlight={this.store.degreeHighlight}
              onChange={this.handleDegreeChange}
              options={['Bachelor', 'Master', 'PhD', 'Other']}
              value={this.store.degree}
          />
          <InputField
              label="Grad Year"
              layoutClassName="column is-6"
              helpMessage={this.store.gradYearHelpMessage}
              highlight={this.store.gradYearHighlight}
              name="gradYear"
              onChange={this.handleGradYearChange}
              placeholder="2008"
              type="number"
              value={gradYearString}
          />
        </div>

        <div className="columns">
          <InputField
              label="Major"
              layoutClassName="column is-12"
              helpMessage={this.store.majorHelpMessage}
              highlight={this.store.majorHighlight}
              name="major"
              onChange={this.handleMajorChange}
              placeholder="Computer Science"
              type="text"
              value={this.store.major}
          />
        </div>

        <div className="columns modal-buttons">
          <div className="column">
            <button
                className="button"
                disabled={!!this.store.awaitServer}
                onClick={this.closeModal}
            >
              Cancel
            </button>
          </div>
          <div className="column">
            <button
                className={`button is-primary ${this.store.awaitServer && 'is-loading'}`}
                disabled={!!this.store.awaitServer}
                onClick={this.handleSaveClick}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  @action
  private handleDegreeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.store.degree = event.currentTarget.value;
    this.store.degreeBlurred = true;
  }

  @action
  private handleGradYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.store.gradYear = +event.currentTarget.value;
    this.store.gradYearBlurred = true;
  }

  @action
  private handleMajorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.store.major = event.currentTarget.value;
    this.store.majorBlurred = true;
  }

  @action
  private handleSchoolChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.store.school = event.currentTarget.value;
    this.store.schoolBlurred = true;
  }

  private handleEditClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    const blockId = event.currentTarget.attributes.getNamedItem('data-block-id');
    if (blockId) {
      this.showModal(+blockId.value);
    }
  }

  @action
  private handleDeleteClick = async (event: React.MouseEvent<HTMLSpanElement>) => {
    const blockId = event.currentTarget.attributes.getNamedItem('data-block-id');
    if (blockId) {
      const educations = this.store.educations;
      const deleted = educations.splice(+blockId.value, 1);

      try {
        await request.delete(`/educations/${deleted[0].id}`);
        runInAction(() => this.store.educations = educations);
      } catch (err) {
        alert('Unable to delete; please try again later.');
        console.error(err);
      }
    }
  }

  @action
  private handleSaveClick = async () => {
    if (this.store.checkReadyForSave()) {
      const { edittingBlockId, degree, gradYear, major, school } = this.store;

      const educations: Education[] = this.store.educations;
      const isUpdate = educations.length > edittingBlockId;
      const oldEducation =  educations[edittingBlockId] || {};
      const newEducation = { ...oldEducation, degree, gradYear, major, school };
      educations[edittingBlockId] = newEducation;
      
      const jsonData = { degree, major, school, year_graduated: gradYear };
      this.store.awaitServer = true;
      const requestPromise = isUpdate
          ? request.put(`/educations/${newEducation.id}/`, jsonData)
          : request.post(`/educations/`, jsonData);
      
      const response = await requestPromise;
      runInAction(() => {
        if (response.data.id) {
          educations[edittingBlockId].id = response.data.id;
        }

        this.store.awaitServer = false
      });
      this.closeModal();
    }
  }

  /**
   * Shows the edit modal.
   *
   * @param blockId The block we are edditing; if not supplied, assumes we are adding a new education block.
   */
  @action
  private showModal = (blockId?: number) => {
    const hasBlockId = isNumber(blockId);
    const existingEducations = this.store.educations;

    this.store.edittingBlockId = hasBlockId ? blockId! : existingEducations.length;
    if (existingEducations.length > this.store.edittingBlockId) {
      const oldEducation = existingEducations[this.store.edittingBlockId];
      this.store.degree = oldEducation.degree;
      this.store.gradYear = oldEducation.gradYear;
      this.store.major = oldEducation.major;
      this.store.school = oldEducation.school;
    }

    this.store.isEditModalActive = true;
  }

  /**
   * Clear the editor modal and close it.
   */
  @action
  private closeModal = () => {
    this.store.degree = '';
    this.store.degreeBlurred = false;
    this.store.gradYear = 0;
    this.store.gradYearBlurred = false;
    this.store.major = '';
    this.store.majorBlurred = false
    this.store.school = '';
    this.store.schoolBlurred = false;
    this.store.isEditModalActive = false;
  }
}

export default EducationEditor;



// WEBPACK FOOTER //
// ./src/common/widgets/EducationEditor.tsx